import React from 'react';
import { Link, useParams } from 'react-router-dom';
import { SharedPageLayout } from '../shared/SharedPageLayout';
import killerBlocksCover from './killerblocks/assets/cover.jpg';
import theBankCover from './thebank/assets/cover.jpg';
import styxxerCover from './styxxer/assets/cover.jpg';
import spongCover from './pong/assets/cover.jpg';
import spaceCover from './space/assets/cover.jpg';
import viperCover from './viper/assets/cover.jpg';
import jimmyFrogCover from './jimmyfrog/assets/cover.jpg';
import game1943Cover from './1943/assets/cover.jpg';
import game4096Cover from './4096/assets/cover.jpg';
import ballBallCover from './ballball/assets/cover.jpg';
import bouncingBallCover from './bouncingballs/assets/cover.jpg';
import crazyCarsCover from './crazycars/assets/cover.jpg';
import synthwordCover from './synthword/assets/cover.jpg';
import synthword6Cover from './synthword6/assets/cover.jpg';
import numdleCover from './numdle/assets/cover.jpg';
import './GameDetail.css';

// Stores metadata that powers each game detail page.
const gameDetails = [
  {
    slug: 'killer-blocks',
    title: 'Killer Blocks',
    tagline: 'Neon ice puzzles with a three.js heartbeat.',
    description: 'Slide and smash blocks across a frosted grid while ghost AI hunts you. Every stage is a tight arena where positioning, timing, and block momentum are the only way out.',
    extendedDescription: 'Killer Blocks mixes tactical movement and pressure management: every push changes the arena, so each turn can open escape routes or create traps. You must read enemy trajectories, use block momentum to control space, and build safe patterns before the faster late-stage waves collapse your options.',
    cover: {
      src: killerBlocksCover,
      alt: 'Killer Blocks cover art'
    },
    playRoute: '/killerblock',
    techSummary: [
      'Three.js scene with a PerspectiveCamera, fog, and layered ambient/directional/point lighting for the neon glow.',
      '11x11 grid simulation where blocks slide across cells, collide, and explode into particle shards.',
      'Enemy AI alternates between pursuit and random wander, with level scaling for speed and density.',
      'Web Audio-powered sound effects plus a looping soundtrack that syncs with start/stop state.',
      'Keyboard, swipe, and on-screen D-pad inputs share a unified key-state controller.'
    ],
    gameplayRules: [
      'Move one step at a time to push ice blocks and open a safe path through the grid.',
      'Avoid direct contact with enemy hunters and never get trapped in dead-end corridors.',
      'Clear the current objective for each stage to advance, then adapt to faster and denser layouts.'
    ],
    highlights: [
      {
        title: 'Gameplay loop',
        text: 'The render loop advances by requestAnimationFrame, updates movement timers, resolves collisions, and then renders the scene each frame.'
      },
      {
        title: 'Visual construction',
        text: 'All characters and props are assembled from procedural meshes and materials, giving the game a chunky, handcrafted 3D look.'
      },
      {
        title: 'Progression tuning',
        text: 'Level difficulty scales by shrinking block density, increasing enemy count, and speeding up movement timings.'
      }
    ]
  },
  {
    slug: 'the-bank',
    title: 'The Bank',
    tagline: 'A western reflex shooter built from 2D canvases and neon HUDs.',
    description: 'Three doors, split-second decisions. Spot bandits, spare civilians, and keep your lives intact as the saloon speeds up round after round.',
    extendedDescription: 'The Bank is built around fast recognition under pressure: silhouettes pop in, timings shrink, and mistakes become increasingly expensive. Strong runs depend on rhythm, target discrimination, and keeping composure as civilian fakes and rapid spawns force precise, disciplined shots.',
    cover: {
      src: theBankCover,
      alt: 'The Bank cover art'
    },
    playRoute: '/the-bank',
    techSummary: [
      'Three.js orthographic camera renders layered planes for the saloon, HUD, and door stages.',
      'Doors, characters, and effects are driven by CanvasTexture or PNG assets with UV cover fitting.',
      'A difficulty curve controls spawn intervals, door open times, and bandit probability as time advances.',
      'Input is handled via arrow keys and custom button events that dispatch a shared "gamefire" signal.',
      'Web Audio synthesis handles shots, hits, and alerts while a looping soundtrack keeps tempo.'
    ],
    gameplayRules: [
      'Watch the three doors and react quickly when a character appears.',
      'Shoot bandits to score points, but avoid hitting civilians or you lose lives/tempo.',
      'Survive as long as possible while rounds speed up and enemy pressure increases.'
    ],
    highlights: [
      {
        title: 'Scene pipeline',
        text: 'Static scenery is pre-rendered into canvases, converted to textures, and layered into a single Three.js scene.'
      },
      {
        title: 'Combat feedback',
        text: 'Explosions, floating score text, and screen flash overlays are generated on the fly for each hit or miss.'
      },
      {
        title: 'Score systems',
        text: 'The HUD tracks kills, misses, accuracy, and lives, with score multipliers scaling over time.'
      }
    ]
  },
  {
    slug: 'styxxer',
    title: 'Styxxer',
    tagline: 'A neon Qix-style capture run with analog-smooth control.',
    description: 'Race along the borders, carve trails into the grid, and seal pockets before the hunters cut you down. Capture 75% of the field to level up through a relentless 99-stage climb.',
    extendedDescription: 'Styxxer rewards controlled aggression: short captures are safer, but large enclosures accelerate progression and scoring. You constantly balance greed and survival while enemy routes adapt around your trails, turning each level into a territory-control duel where positioning matters more than pure speed.',
    cover: {
      src: styxxerCover,
      alt: 'Styxxer cover art'
    },
    playRoute: '/styxxer',
    techSummary: [
      'Orthographic Three.js scene renders an instanced grid for clean, fast cell updates.',
      'Grid state tracks open, captured, and trail cells to enforce Qix-style capture rules.',
      'Flood-fill logic seals territory by checking which open cells remain connected to enemies.',
      'Analog joystick and keyboard inputs resolve to a single directional step each tick.',
      'Level progression increases enemy speed and resets the field after each capture target.'
    ],
    gameplayRules: [
      'Travel safely on captured borders, then enter open space to draw a live trail.',
      'Reconnect your trail to a safe edge to claim area and progress toward the capture target.',
      'Do not let enemies touch your trail or character while drawing, or you lose the run/life.'
    ],
    highlights: [
      {
        title: 'Capture logic',
        text: 'When the player reconnects to a captured cell, the trail is sealed and a flood fill marks enclosed open cells as captured.'
      },
      {
        title: 'Movement pacing',
        text: 'A step accumulator converts analog input into discrete grid moves while keeping the motion responsive.'
      },
      {
        title: 'Risk management',
        text: 'Enemies bounce off captured borders but can strike both the player and the active trail, forcing careful timing.'
      }
    ]
  },
  {
    slug: 'sbong',
    title: 'SBONG',
    tagline: 'Minimalist game with smooth mouse and touch control.',
    description: 'Trade lightning-fast volleys against a focused AI opponent while the ball accelerates on every paddle hit. The clean monochrome court keeps all attention on timing, angle, and rally control.',
    extendedDescription: 'SBONG strips everything down to spacing, tempo, and angle creation. Since the ball accelerates during long rallies, defensive control quickly becomes offensive setup: subtle paddle positioning determines spin, trajectory, and whether you can break the AI rhythm before it breaks yours.',
    cover: {
      src: spongCover,
      alt: 'SBONG placeholder cover'
    },
    playRoute: '/sbong',
    techSummary: [
      'React state and refs keep the render frame in sync while the simulation runs at full speed.',
      'A requestAnimationFrame loop computes delta time and advances ball, paddle, and collision physics.',
      'Mouse and touch handlers update a shared target position for smooth paddle tracking.',
      'Ball reset logic centers the playfield, randomizes vertical velocity, and sets the serve direction.',
      'AI tracking mirrors the player movement rules with a tuned speed cap for fair rallies.'
    ],
    gameplayRules: [
      'Move your paddle vertically to intercept the ball before it crosses your side.',
      'Aim hits with paddle position to change rebound angle and control the rally.',
      'Score when the AI misses and defend every return as ball speed ramps up over time.'
    ],
    highlights: [
      {
        title: 'Control feel',
        text: 'Player movement eases toward a target so the paddle feels responsive without snapping instantly.'
      },
      {
        title: 'Rally pacing',
        text: 'Each paddle hit boosts horizontal speed and adds spin based on the impact position.'
      },
      {
        title: 'Responsive layout',
        text: 'The board reads container size on resize events to keep gameplay consistent across devices.'
      }
    ]
  },
  {
    slug: 'space',
    title: 'Galaxy Assault',
    tagline: 'Classic Arcade rebuilt with neon shields and escalating waves.',
    description: 'Defend the frontier by sweeping invader ranks, breaking through shield blocks, and hunting down the mystery ship. Each cleared wave accelerates the swarm while your score and lives persist across levels.',
    extendedDescription: 'Galaxy Assault emphasizes lane control and threat prioritization: trimming front-line invaders can buy time, while shield preservation creates safer firing windows during dense bullet phases. As cadence increases, survival depends on balancing riskier score plays with disciplined positioning and cooldown timing.',
    cover: {
      src: spaceCover,
      alt: 'Galaxy Assault cover art'
    },
    playRoute: '/space',
    techSummary: [
      'Orthographic Three.js scene with custom meshes for the player, invaders, and shield blocks.',
      'Wave manager escalates invader speed, firing cadence, and drop behavior per level.',
      'Unified keyboard and touch input maps to ship movement plus timed fire cooldowns.',
      'Projectile collision checks handle invaders, shields, and player hit flashes.',
      'Particle cube explosions and starfield points create the neon arcade ambience.'
    ],
    gameplayRules: [
      'Move left and right to line up shots and avoid enemy projectiles.',
      'Destroy invader rows while using shields as temporary cover that degrades with damage.',
      'Clear every wave to advance; survive enemy hits with remaining lives to continue the campaign.'
    ],
    highlights: [
      {
        title: 'Invader cadence',
        text: 'A dynamic interval shortens as enemies thin out, recreating the classic accelerating march.'
      },
      {
        title: 'Shield attrition',
        text: 'Each shield tile tracks hit points and fades before disappearing, letting defensive cover erode over time.'
      },
      {
        title: 'Wave continuity',
        text: 'Score, lives, and level counters persist through the win screen so each wave feels like a true campaign.'
      }
    ]
  },
  {
    slug: '1943',
    title: '1943',
    tagline: 'A synthwave dogfight with endless neon skies.',
    description: 'Pilot a warplane through rolling enemy formations, dodge incoming fire, and clear ten waves of squadrons to finish the mission. Bright neon exhaust, pulsing lights, and explosive particles keep the arcade energy high from takeoff to victory.',
    extendedDescription: '1943 combines route planning and sustained dodging: enemy patterns overlap, forcing you to predict safe lanes before bullets saturate the screen. Efficient pilots use short bursts, controlled lateral movement, and wave-aware positioning to preserve lives for the toughest final formations.',
    cover: {
      src: game1943Cover,
      alt: '1943 cover art'
    },
    playRoute: '/1943',
    techSummary: [
      'Orthographic Three.js scene layers fog, point lights, and a scrolling wireframe grid for the retro sky runway.',
      'Player movement uses velocity damping, banking rotations, and invincibility flashes to mimic classic arcade flight handling.',
      'Wave spawner builds enemy formations with sine, cosine, and spiral movement patterns that descend across the screen.',
      'Projectile system manages player and enemy bullets with glow meshes and collision checks for hits and misses.',
      'Score, lives, and win state persist across waves while the looped soundtrack keeps momentum between levels.'
    ],
    gameplayRules: [
      'Pilot the plane across the arena while continuously dodging incoming enemy fire.',
      'Shoot enemy squadrons to clear each wave and prevent screen overwhelm.',
      'Protect your lives through all mission waves; complete the full sequence to win.'
    ],
    highlights: [
      {
        title: 'Squadron waves',
        text: 'Enemy formations scale up each wave, mixing movement patterns, fire rates, and speeds to keep the dogfight evolving.'
      },
      {
        title: 'Neon flight deck',
        text: 'The background grid scrolls continuously while starfields rotate subtly, creating forward motion without camera moves.'
      },
      {
        title: 'Combat feedback',
        text: 'Explosions spawn particle bursts with fading opacity and scale to sell every hit and collision.'
      }
    ]
  },
  {
    slug: 'jimmy-frog',
    title: 'Jimmy Frog',
    tagline: 'Cross the neon highway, ride the glowing logs, reach all five lily pads.',
    description: 'A synthwave Frogger revival where you dodge fluo cars on the neon road, drift on glowing logs over the electric river, and land on every lily pad to level up. Speed increases with each cleared board.',
    extendedDescription: 'Jimmy Frog is a timing puzzle in motion: roads test reaction windows while river lanes demand anticipation of drifting platforms. Expert play comes from planning two or three jumps ahead, syncing with lane cycles, and preserving momentum so each board clear feels deliberate, not accidental.',
    cover: {
      src: jimmyFrogCover,
      alt: 'Jimmy Frog cover art'
    },
    playRoute: '/jimmyfrog',
    techSummary: [
      'Three.js orthographic camera renders a full 2D scene with colored plane meshes and edge geometry for neon borders.',
      'Thirteen-row grid alternates safe zones, road lanes, and river lanes with independent speed and direction per lane.',
      'Frog drifts automatically with log movement while on a river row; falling off triggers a water death.',
      'Particle burst system spawns on death and pad capture using CircleGeometry with fading opacity.',
      'Keyboard, swipe, and on-screen D-pad all funnel into a single moveFrog call for unified input handling.'
    ],
    gameplayRules: [
      'Cross roads by timing jumps between moving cars without getting hit.',
      'On river lanes, jump on logs and ride them safely; falling in water costs a life.',
      'Occupy all five lily pads to clear the board and reach the next faster level.'
    ],
    highlights: [
      {
        title: 'Lane system',
        text: 'Each road and river lane has its own speed, direction, and obstacle width. River obstacles carry the frog while road obstacles kill on contact.'
      },
      {
        title: 'Level progression',
        text: 'Filling all five lily pads advances the level and multiplies obstacle speed, keeping the challenge steep across every board.'
      },
      {
        title: 'Synthwave visuals',
        text: 'Neon emissive colors, edge outlines, grid lines, and pulsing pad rings recreate the fluo 1980s arcade aesthetic entirely in Three.js.'
      }
    ]
  },
  {
    slug: 'viper',
    title: 'Viper',
    tagline: 'A synthwave Snake clone that glows with every bite.',
    description: 'Pilot the neon serpent across a wraparound grid, snagging pulsating energy diamonds while the tempo climbs. One wrong turn into your tail detonates the run in a burst of retro particles.',
    extendedDescription: 'Viper is all about route economy: every pickup expands your body and reduces future maneuvering space, so pathing efficiency is critical. The wraparound board creates creative escapes, but also deceptive traps, rewarding players who can map safe loops under increasing speed pressure.',
    cover: {
      src: viperCover,
      alt: 'Viper cover art'
    },
    playRoute: '/viper',
    techSummary: [
      'Canvas-driven renderer draws the synthwave sun, neon grid, and scanline overlays every frame.',
      'Fixed-step tick updates the snake, wraparound movement, and collision checks at arcade cadence.',
      'Food spawns avoid occupied cells via a grid lookup to keep each bite fair and fast.',
      'Keyboard, swipe, and on-screen D-pad inputs funnel into a shared next-direction state.',
      'A looping soundtrack plays continuously without resetting between levels to keep the flow steady.'
    ],
    gameplayRules: [
      'Guide the snake to collect energy items and increase both length and score.',
      'Use quick turns but avoid reversing into your own body or colliding with it.',
      'Stay alive as speed and board pressure rise, extending the run for a higher score.'
    ],
    highlights: [
      {
        title: 'Neon presentation',
        text: 'The playfield layers a retro sun, glowing grid lines, and particle bursts for every crash and bite.'
      },
      {
        title: 'Snake handling',
        text: 'Direction changes are buffered to avoid instant reversals while wraparound keeps the momentum flowing.'
      },
      {
        title: 'Touch-ready play',
        text: 'Swipe gestures and a neon D-pad mirror keyboard input so mobile sessions feel just as tight.'
      }
    ]
  },
  {
    slug: '4096',
    title: '4096/2',
    tagline: 'A synthwave puzzle game with neon merges and endless momentum.',
    description: 'Slide neon tiles across a glowing grid, chain merges, and build the score as you chase the 4096 block. Responsive swipes, punchy merges, and a looping soundtrack keep the momentum steady while every move raises the stakes.',
    extendedDescription: '4096/2 rewards long-term structure over short-term gains: careless merges can collapse your high-value corner and lock the board. Consistent runs rely on maintaining a directional strategy, building merge ladders, and controlling random tile pressure without breaking your board architecture.',
    cover: {
      src: game4096Cover,
      alt: '4096 cover art'
    },
    playRoute: '/4096',
    techSummary: [
      'Grid logic builds from a tile list, merging equal values and adding a random new tile after each valid move.',
      'Merge resolution animates by staging source tiles, combining values, and cleaning up merged nodes after a short delay.',
      'Keyboard arrows and swipe gestures map to the same move handler for parity across desktop and mobile.',
      'Responsive sizing scales the grid and tile spacing based on the current window width.',
      'A looping soundtrack plays continuously without resetting to keep the run flowing between merges.'
    ],
    gameplayRules: [
      'Slide all tiles in one direction each turn using arrows or swipes.',
      'Merge matching numbers to create larger values and free board space.',
      'Reach the 4096 target tile while preventing the grid from filling completely.'
    ],
    highlights: [
      {
        title: 'Merge pipeline',
        text: 'Each move collapses tiles along the chosen axis, merges equal values once per step, and updates the score for every successful combo.'
      },
      {
        title: 'Input rhythm',
        text: 'Swipe detection compares horizontal and vertical deltas to choose the intended move while sharing the same logic as keyboard inputs.'
      },
      {
        title: 'Adaptive layout',
        text: 'Tile size and gaps adapt to screen width so the grid stays playable on phones without losing the neon aesthetic.'
      }
    ]
  },
  {
    slug: 'ballball',
    title: 'Ball Ball',
    tagline: 'A neon maze chase with power-ups, ghosts, and synthwave glow.',
    description: 'Guide the glowing sphere through a synthwave maze, eat every dot, and trigger power-ups to hunt the ghosts. Each cleared board speeds up the chase while score, levels, and menu states keep the arcade loop intact.',
    extendedDescription: 'Ball Ball alternates between evasion and control phases: standard mode rewards clean pathing and corner awareness, while power windows invite aggressive ghost hunting for score spikes. As speed ramps, route memorization and decision timing become the core skill ceiling.',
    cover: {
      src: ballBallCover,
      alt: 'Ball Ball cover art'
    },
    playRoute: '/ballball',
    techSummary: [
      'Three.js scene uses a PerspectiveCamera, ambient/directional lighting, and neon fog for the arcade look.',
      'Procedural maze walls, dots, and power-ups are spawned as meshes with per-level density scaling.',
      'Ghost AI swaps between random drift and flee behavior when the power-up timer is active.',
      'Looped soundtrack playback runs continuously while SFX hit events trigger individual Howler sounds.',
      'Keyboard arrows and swipe gestures resolve into a shared direction state for consistent movement.'
    ],
    gameplayRules: [
      'Navigate the maze and eat all dots to complete the current board.',
      'Avoid ghosts in normal state; use power-ups to turn the chase in your favor.',
      'Clear successive levels with increasing speed while preserving lives and maximizing score.'
    ],
    highlights: [
      {
        title: 'Level flow',
        text: 'Clearing all dots increments the level, raises speed values, and respawns the maze pickups after a short pause.'
      },
      {
        title: 'Power-up state',
        text: 'Invincibility toggles a glow shell, flips ghost behavior, and counts down with a timed interval tick.'
      },
      {
        title: 'Ghost feedback',
        text: 'Defeated ghosts explode into particle bursts that fade out through a requestAnimationFrame loop.'
      }
    ]
  }
  ,
  {
    slug: 'crazy-cars',
    title: 'Crazy Cars',
    tagline: 'A synthwave highway survival run with curved-road illusion and neon traffic.',
    description: 'Drive a glowing racer across a shifting neon motorway, dodge incoming cars, and keep your run alive as speed and road sway pressure your steering. One collision ends the ride, so lane control and rhythm are everything.',
    extendedDescription: 'Crazy Cars focuses on anticipation rather than raw reaction: traffic recycles continuously and road sway distorts your visual center, so overcorrection is the main danger. Stable runs come from smooth lane transitions, early reads, and staying calm through denser spawn cycles.',
    cover: {
      src: crazyCarsCover,
      alt: 'Crazy Cars cover art'
    },
    playRoute: '/crazycars',
    techSummary: [
      'React Three Fiber scene uses a forward-moving synthwave grid texture with sinusoidal horizontal offset to fake road curvature.',
      'Player car position is smoothed with THREE.MathUtils.lerp and subtle bob/tilt animation to reinforce speed and steering feedback.',
      'Obstacle pool recycles traffic meshes along the Z axis, randomizing lane X positions for continuous arcade flow.',
      'Collision detection checks obstacle depth in the player zone and compares X distance to trigger game-over state.',
      'Keyboard arrows and touch drag steering both update the same lane position ref for consistent desktop/mobile control.'
    ],
    gameplayRules: [
      'Steer the car left or right to stay clear of incoming traffic.',
      'Read road sway and obstacle patterns early to avoid late corrections.',
      'Keep the run alive as speed and density increase; one collision ends the game.'
    ],
    highlights: [
      {
        title: 'Road illusion',
        text: 'The terrain updates texture offsets every frame, combining forward scroll with a sine-based lateral shift so the highway appears to bend left and right.'
      },
      {
        title: 'Traffic loop',
        text: 'Enemy cars move toward the camera and recycle to the horizon when they pass the player, maintaining nonstop pressure without heavy spawn logic.'
      },
      {
        title: 'Steering feel',
        text: 'The player car uses interpolated movement and banking rotation, creating smoother arcade handling than direct position snapping.'
      }
    ]
  },
  {
    slug: 'bouncing-ball',
    title: 'Bouncing Ball',
    tagline: 'A Three.js Breakout revival with neon bricks, a synthwave paddle, and particle explosions.',
    description: 'Control a glowing cyan paddle at the bottom of a synthwave arena, deflect the magenta ball, and smash every neon brick into particles. Each brick hit scores ten points and detonates a burst of white sparks. Lose all three lives when the ball drops below the floor.',
    extendedDescription: 'Bouncing Ball turns simple rules into precision control: the paddle impact point sets ball angle, enabling targeted clears and combo-like lane openings through brick clusters. Mastery depends on trajectory planning, recovery positioning, and avoiding panic hits when speed and bounce variance rise.',
    cover: {
      src: bouncingBallCover,
      alt: 'Bouncing Ball cover art'
    },
    playRoute: '/bouncingball',
    techSummary: [
      'Three.js PerspectiveCamera scene with ambient and point lighting for the neon glow effect.',
      'Synthwave GridHelper rotated on the XY plane forms the glowing floor background.',
      'Ball physics use a THREE.Vector3 velocity updated each animation frame with wall and ceiling bounce.',
      'Bricks are procedurally generated in a 5×5 color-cycled grid using BoxGeometry and MeshStandardMaterial.',
      'Particle system spawns twenty points at a brick position and removes them after 300 ms for a quick burst effect.',
      'Keyboard and touch swipe inputs drive paddle movement via a shared direction ref, clamped to the arena bounds.',
      'Looping soundtrack plays continuously without resetting between rounds, consistent with the app audio pattern.'
    ],
    gameplayRules: [
      'Move the paddle to keep the ball in play and direct rebounds toward brick clusters.',
      'Break all bricks to clear the stage and maximize score with continuous control.',
      'Do not let the ball fall below the paddle; losing all lives ends the run.'
    ],
    highlights: [
      {
        title: 'Ball and paddle physics',
        text: 'The ball reflects off walls and ceiling automatically. Hitting the paddle adds a spin offset based on the horizontal distance from the paddle center, adding angle control for the player.'
      },
      {
        title: 'Brick destruction effect',
        text: 'Each brick hit removes the mesh from the scene, spawns a particle cloud at the same position, and auto-clears it after a short delay, keeping the arena clean between shots.'
      },
      {
        title: 'Input parity',
        text: 'Arrow-key and touch-swipe inputs both write to the same moveDir ref, so desktop and mobile sessions play identically with no extra code paths.'
      }
    ]
  },
  {
    slug: 'synthword',
    title: 'Synthword',
    tagline: 'A neon word puzzle where every guess lights up the grid.',
    description: 'Find the hidden five-letter word in six tries using keyboard and touch controls, with bilingual IT/EN dictionaries and instant color feedback for each letter. The loop is clean and fast, built for replayable arcade-style sessions.',
    extendedDescription: `Synthword, a Wordle inspired game, is a deduction loop with strong information economy: each guess should test hypotheses, not just chase partial matches. By combining color feedback with frequency logic and letter-position constraints, players can narrow candidate sets rapidly and maintain consistent solve rates.

    <p>Wordle is a daily word-guessing game built around deduction, information theory, and constrained search. Each day, everyone gets the same hidden five-letter English word (the “answer”) and has a limited number of attempts to find it. After each guess, the game returns feedback for every letter, letting you narrow down the solution space until you either identify the word or run out of tries.</p>
<h2 id="origins-and-background">Origins and background</h2>
<p>Wordle was created by Josh Wardle in 2021 as a private game for his partner, then released publicly in October 2021. It spread rapidly in late 2021 and early 2022, helped by its simple rules, the once-per-day format (which avoids endless grinding), and the ability to share results as spoiler-free colored emoji grids. In early 2022 it was acquired by The New York Times, which now hosts and maintains it (with minor changes over time such as curation of valid guesses and answers).</p>
<h2 id="core-logic-feedback-rules">Core logic: feedback rules</h2>
<p>You have 6 guesses to find a 5-letter word.</p>
<p>After each guess, each letter is marked:</p>
<ul>
<li><strong>Green</strong>: the letter is correct and in the correct position.</li>
<li><strong>Yellow</strong>: the letter is in the answer but in a different position.</li>
<li><strong>Gray</strong>: the letter is not in the answer (with an important nuance for repeated letters).</li>
</ul>
<h3 id="repeated-letter-nuance-key-edge-case">Repeated-letter nuance (key edge case)</h3>
<p>Wordle’s coloring follows a “counting” rule: a letter can only be marked green/yellow as many times as it actually appears in the answer.</p>
<p>Example:</p>
<ul>
<li>Answer: <strong>APPLE</strong> (A×1, P×2, L×1, E×1)</li>
<li>Guess: <strong>PUPPY</strong>
<ul>
<li>The guess contains <strong>P</strong> three times, but the answer contains <strong>P</strong> only twice.</li>
<li>Wordle will mark at most two of those P’s as green/yellow (depending on positions) and the remaining P as gray.</li>
</ul>
</li>
</ul>
<p>A common tricky case:</p>
<ul>
<li>Answer: <strong>LEVEL</strong></li>
<li>Guess: <strong>LEEEE</strong>
<ul>
<li>The answer has E×2, but the guess has E×4.</li>
<li>Only two E’s can receive non-gray feedback.</li>
</ul>
</li>
</ul>
<p>This rule matters because “gray” doesn’t always mean “this letter never appears”; it can mean “you’ve already used up the allowed count of that letter.”</p>
<h2 id="how-to-play-practical-workflow">How to play (practical workflow)</h2>
<ol>
<li><strong>Start with an opening guess</strong> designed to maximize information (common letters, good vowel coverage, minimal repeats).</li>
<li><strong>Interpret the feedback</strong>:
<ul>
<li>Lock greens into place.</li>
<li>Track yellows as “must include, but not here.”</li>
<li>Treat grays as “exclude,” except when repeated-letter counting is in play.</li>
</ul>
</li>
<li><strong>Generate candidate words</strong> consistent with all constraints.</li>
<li><strong>Choose the next guess</strong> to either:
<ul>
<li><strong>Solve</strong> if the candidate set is small, or</li>
<li><strong>Split the remaining candidates</strong> efficiently by testing letters/positions that differentiate them best.</li>
</ul>
</li>
</ol>
<h2 id="common-edge-cases-and-pitfalls">Common edge cases and pitfalls</h2>
<h3 id="hard-mode-constraints">1) “Hard mode” constraints</h3>
<p>In Wordle’s optional Hard Mode, any revealed hints must be used in subsequent guesses:</p>
<ul>
<li>Greens must stay in the same position.</li>
<li>Yellows must be included somewhere (not necessarily the same spot).
This removes some probing strategies (like making a “test word” that ignores known placements) and increases the importance of careful letter placement.</li>
</ul>
<h3 id="trap-situations-many-similar-candidates">2) “Trap” situations (many similar candidates)</h3>
<p>Some answers belong to dense families that differ by one letter:</p>
<ul>
<li>_-ATCH: <strong>CATCH, HATCH, LATCH, MATCH, PATCH, WATCH</strong></li>
<li>_-IGHT: <strong>LIGHT, MIGHT, NIGHT, RIGHT, SIGHT, TIGHT</strong>
If you reach a point where multiple candidates remain and you only have a few guesses left, the best move can be a <strong>disambiguation guess</strong> that uses several of the differing letters at once, even if that guess isn’t itself a likely answer (unless playing with stricter rules or personal constraints).</li>
</ul>
<h3 id="valid-guess-list-vs.answer-list">3) Valid guess list vs. answer list</h3>
<p>Wordle accepts many valid guesses (including uncommon words), but the set of possible “answers” is smaller and curated. A guess can be legal yet extremely unlikely as an answer. Strong play uses allowed guesses to extract information, but also keeps in mind what words are plausible solutions.</p>
<h3 id="repeated-letters-in-the-answer">4) Repeated letters in the answer</h3>
<p>Many players under-test duplicates early. Some puzzles hinge on a double letter (e.g., <strong>SHEEP</strong>, <strong>BOOST</strong>, <strong>ALLOT</strong>, <strong>ENNUI</strong> in broader variants). Once you have strong evidence for a repeated letter, testing duplication can be crucial.</p>
<h2 id="best-strategy-conceptual">Best strategy (conceptual)</h2>
<p>From an information perspective, the goal is to <strong>maximize expected reduction in the candidate set</strong> each turn. In simpler terms:</p>
<ul>
<li>Early guesses should cover <strong>frequent letters</strong> and <strong>vowels</strong>, avoid repeats, and create diverse positional tests.</li>
<li>Midgame guesses should <strong>target unresolved letters</strong> and confirm placements.</li>
<li>Endgame should prioritize <strong>guaranteed solve paths</strong> over “clever” guesses.</li>
</ul>
<h3 id="practical-principles">Practical principles</h3>
<ul>
<li>Prefer guesses with <strong>5 distinct letters</strong> early.</li>
<li>Include common consonants: <strong>R, T, N, S, L, C, D, P</strong> (frequency varies by source but these are generally strong).</li>
<li>Cover vowels early: <strong>A, E, I, O, U</strong> (often A/E are most valuable; U is less frequent but can matter).</li>
<li>Avoid rare letters early unless forced: <strong>J, Q, X, Z</strong>.</li>
<li>Don’t lock into a word pattern too soon if many candidates remain; use a probe word to eliminate several possibilities at once.</li>
</ul>
<h2 id="strong-opening-words-first-guess">Strong opening words (first guess)</h2>
<p>There is no single universally “best” opener for every rule set, but good openers share: common letters, multiple vowels, no repeats.</p>
<p>Popular strong openers include:</p>
<ul>
<li><strong>SLATE</strong> (common letters, two vowels)</li>
<li><strong>CRANE</strong> (excellent frequency coverage)</li>
<li><strong>TRACE</strong></li>
<li><strong>SALET</strong> (not always a common answer, but strong as a guess)</li>
<li><strong>ARISE</strong></li>
<li><strong>RAISE</strong></li>
<li><strong>STARE</strong></li>
<li><strong>LEAST</strong></li>
<li><strong>ADIEU</strong> (lots of vowels, but weaker consonant info than options like SLATE/CRANE)</li>
</ul>
<p>If you want a highly practical approach:</p>
<ul>
<li>Use <strong>SLATE</strong> or <strong>CRANE</strong> as a balanced default.</li>
<li>If the first guess yields few clues, follow with a second guess that complements it by covering remaining high-frequency letters.</li>
</ul>
<h3 id="two-guess-coverage-approach-example-idea">Two-guess “coverage” approach (example idea)</h3>
<p>Pick two words that together cover ~10 distinct, common letters with good vowel spread. One common pairing concept (many variations exist):</p>
<ul>
<li><strong>CRANE</strong> + <strong>SPLIT</strong> (covers C,R,A,N,E,S,P,L,I,T)
Or:</li>
<li><strong>SLATE</strong> + <strong>CRONY</strong> (covers S,L,A,T,E,C,R,O,N,Y)</li>
</ul>
<p>Exact best pairs depend on whether you allow guesses that aren’t likely answers and whether you play Hard Mode.</p>
<h2 id="midgame-decision-making-solve-vs.probe">Midgame decision-making: solve vs. probe</h2>
<ul>
<li><strong>If ≤2–3 candidates remain</strong> and you have enough guesses, start attempting direct solutions.</li>
<li><strong>If 4–6+ candidates remain</strong> and they share a tight pattern, use a probe word that:
<ul>
<li>includes as many of the “branching” letters as possible, and</li>
<li>respects known constraints (especially in Hard Mode).</li>
</ul>
</li>
</ul>
<p>Example trap:
You have _ATCH with many possibilities and only 2–3 guesses left. Instead of guessing <strong>HATCH</strong> then <strong>WATCH</strong> then losing to <strong>MATCH</strong>, you might play a probe like <strong>CLAMP</strong> (in variants) or another allowed guess to test multiple distinguishing letters at once. In standard Wordle you must still use a valid 5-letter word; the key is letter coverage, not “being a candidate.”</p>
<h2 id="curious-facts-and-cultural-notes">Curious facts and cultural notes</h2>
<ul>
<li>The shareable grid (colored squares) was a major viral mechanic: it communicates progress without revealing the word.</li>
<li>The once-per-day limit creates a communal “same puzzle” experience and prevents optimal play from being reduced to brute-force repetition.</li>
<li>Wordle inspired a large ecosystem of spin-offs (different word lengths, languages, math variants like Nerdle, geography/music versions, etc.).</li>
<li>Many players track streaks and average guesses; strong strategic play often aims for a consistent 3–4 guess average rather than occasional 2s with occasional failures.</li>
</ul>
`,
    cover: {
      src: synthwordCover,
      alt: 'Synthword cover art'
    },
    playRoute: '/synthword',
    techSummary: [
      'State-driven React architecture tracks guesses, evaluations, keyboard statuses, and win/lose flow in one synchronized loop.',
      'Two-pass guess evaluation marks exact matches first, then present letters, to keep repeated-letter results accurate.',
      'Virtual keyboard, physical keyboard, and touch interactions all route through the same input handler for consistent behavior.',
      'Language toggle swaps dictionaries and UI copy between Italian and English while preserving the same gameplay rules.',
      'Looping soundtrack playback is initialized once from /features/games/synthword/assets/soundtrack.mp3 and kept continuous without reset between rounds.'
    ],
    gameplayRules: [
      'Guess the hidden five-letter word in up to six attempts.',
      'Use tile colors after each guess to understand correct letters, positions, and exclusions.',
      'Refine each next guess with keyboard clues until you solve the word before attempts run out.'
    ],
    highlights: [
      {
        title: 'Feedback clarity',
        text: 'Every submitted row flips tile-by-tile and updates keyboard key states, so correct, present, and absent letters stay readable at a glance.'
      },
      {
        title: 'Cross-input parity',
        text: 'On-screen controls and physical keyboard events share the same logic path, preventing desktop/mobile behavior drift.'
      },
      {
        title: 'Continuous audio flow',
        text: 'The synthwave soundtrack loops continuously and is not restarted during replay cycles, keeping session momentum stable.'
      }
    ]
  },
  {
    slug: 'synthword-6-version',
    title: 'Synthword 6 letters',
    tagline: 'A six-letter neon word puzzle with bilingual mode and arcade feedback.',
    description: 'Decode the hidden six-letter word in six attempts with instant tile states, animated reveal timing, and a full synthwave interface. The game supports Italian and English dictionaries, on-screen keys, and physical keyboard input, keeping desktop and mobile play in sync.',
    extendedDescription: `
Synthword 6 Letters, a Wordle inspired game, is a deduction loop with strong information economy: each guess should test hypotheses, not just chase partial matches. By combining color feedback with frequency logic and letter-position constraints, players can narrow candidate sets rapidly and maintain consistent solve rates.

    <p>Wordle is a daily word puzzle in which you have a limited number of attempts to guess a hidden word. After each guess, the game provides feedback on every letter to indicate how close your guess is to the solution. The core appeal is the blend of deduction and probability: you continuously narrow the space of possible answers based on the information you gain.</p>
<h2 id="origins-and-evolution">Origins and evolution</h2>
<p>Wordle was created by Josh Wardle and released publicly in 2021 as a simple web-based game with one puzzle per day. It spread rapidly through social sharing—especially via its colored grid results—then was acquired by The New York Times in early 2022. Many variants followed, changing word length, number of guesses, languages, themes, and constraints. This version uses <strong>6-letter words</strong>, which meaningfully changes strategy: there are more letter slots, more combinations, and often a larger candidate set.</p>
<h2 id="the-logic-of-the-feedback-with-6-letters">The logic of the feedback (with 6 letters)</h2>
<p>Each guess must be a valid <strong>6-letter</strong> word. After submitting, each of the 6 letters is marked:</p>
<ul>
<li><strong>Green</strong>: the letter is correct and in the correct position.</li>
<li><strong>Yellow</strong>: the letter appears in the solution but is in a different position.</li>
<li><strong>Gray</strong>: the letter does not appear in the solution <strong>or</strong> you have guessed it more times than it occurs in the solution.</li>
</ul>
<h3 id="duplicate-letter-rules-important-edge-cases">Duplicate-letter rules (important edge cases)</h3>
<p>Handling repeats is the most common source of confusion:</p>
<ul>
<li>If the solution contains a letter <strong>once</strong> and your guess uses it <strong>twice</strong>, only one instance can be green/yellow; the extra one becomes gray.</li>
<li>If the solution contains a letter <strong>twice</strong>, and you guess it twice, both can be colored (depending on positions).</li>
<li>Coloring is effectively constrained by the solution’s letter counts: feedback never implies more copies of a letter than actually exist.</li>
</ul>
<p>Example idea (not tied to a specific language): if the solution has one “A” and your guess has two “A”s, only one “A” can be non-gray.</p>
<h2 id="how-you-play-6-letter-format">How you play (6-letter format)</h2>
<ol>
<li>Enter a <strong>6-letter</strong> guess.</li>
<li>Use the color feedback to:
<ul>
<li>Fix greens in place.</li>
<li>Move yellows to different positions (and remember they cannot be in the position where they were yellow).</li>
<li>Exclude grays (unless a duplicate-letter situation explains them).</li>
</ul>
</li>
<li>Repeat until you find the solution or run out of attempts.</li>
</ol>
<p>Many 6-letter versions allow 6 or 7 guesses; the exact count changes difficulty, but the reasoning process stays the same.</p>
<h2 id="common-edge-cases-and-pitfalls">Common edge cases and pitfalls</h2>
<ul>
<li><strong>“Yellow traps”</strong>: You know a set of letters are in the word but keep placing them in forbidden positions. Keep a clear list of “letter X cannot be in positions {…}”.</li>
<li><strong>Repeated-letter ambiguity</strong>: A gray letter isn’t always “not in the word”; it can mean “not in the word that many times.” Track minimum/maximum counts when possible.</li>
<li><strong>Multiple valid solutions remaining</strong>: Sometimes your constraints still allow many candidates (e.g., <em>A_E_R</em>). In 6-letter mode, these “forks” can be larger. You may need a guess designed to split possibilities rather than immediately solve.</li>
<li><strong>Hard Mode (if enabled)</strong>: You must reuse revealed information (greens stay, yellows must be included). This reduces freedom to “probe” with exploratory words and makes early choices more consequential.</li>
</ul>
<h2 id="best-overall-strategy-adapted-to-6-letters">Best overall strategy (adapted to 6 letters)</h2>
<p>The optimal approach mixes <strong>information gain</strong> (entropy) and <strong>constraint satisfaction</strong>:</p>
<h3 id="start-with-high-information-guesses">1) Start with high-information guesses</h3>
<p>In 6-letter Wordle, you have an extra letter slot, so you can cover more distinct letters early. Prioritize:</p>
<ul>
<li><strong>6 different letters</strong> (avoid repeats early).</li>
<li><strong>Common letters</strong> in the language (especially vowels and frequent consonants).</li>
<li><strong>Common digrams</strong> (letter pairs) can help, but letter coverage usually matters more at the start.</li>
</ul>
<h3 id="use-a-second-coverage-word-when-needed">2) Use a second “coverage” word when needed</h3>
<p>If your first guess yields few clues (many grays), a second guess that uses mostly new letters is often best. Early on, your goal is to identify the solution’s letter set quickly.</p>
<h3 id="only-then-pivot-to-solving">3) Only then pivot to solving</h3>
<p>Once you have:</p>
<ul>
<li>2–4 confirmed letters (greens/yellows),</li>
<li>and a good set of excluded letters,
start making guesses that respect positional constraints and aim to land the exact arrangement.</li>
</ul>
<h3 id="when-stuck-in-a-large-candidate-set-play-a-splitter">4) When stuck in a large candidate set, play a “splitter”</h3>
<p>A splitter is a guess chosen to <strong>test</strong> several uncertain letters at once, even if it might not be a likely solution. In 6-letter puzzles, this is especially valuable because candidate sets can remain large longer.</p>
<h2 id="what-first-words-should-you-use-6-letters">What first words should you use (6 letters)?</h2>
<p>Because the best starters depend on the allowed dictionary and language, the safest guidance is principle-based:</p>
<p><strong>Good 6-letter starters typically:</strong></p>
<ul>
<li>contain <strong>2–3 vowels</strong> (to quickly map vowel structure),</li>
<li>include frequent consonants (often: R, S, T, N, L, D, C, P depending on language),</li>
<li>avoid rare letters (unless your language makes them common),</li>
<li>avoid duplicate letters on the first move.</li>
</ul>
<h3 id="practical-starter-templates">Practical starter templates</h3>
<ul>
<li><strong>Vowel-rich</strong>: patterns like <em>A E I</em> + three common consonants</li>
<li><strong>Balanced</strong>: two vowels + four common consonants</li>
<li><strong>Coverage pair</strong>: two starting words that together cover ~10–12 distinct high-frequency letters with minimal overlap.</li>
</ul>
<p>If you want a strong general approach: pick two 6-letter words whose combined letters cover many of the most common letters in your language, e.g., 3 vowels across the two words and a spread of top consonants, with few repeats between the two guesses.</p>
<h2 id="tactics-for-mid-game-decisions">Tactics for mid-game decisions</h2>
<ul>
<li><strong>Lock greens immediately</strong>: treat them as fixed.</li>
<li><strong>Track yellow exclusions by position</strong>: “E is in the word, but not at position 2.”</li>
<li><strong>Infer letter counts</strong>: if you get one yellow “E” and another “E” is gray, the solution likely contains exactly one E.</li>
<li><strong>Prefer guesses that test multiple uncertainties</strong>: especially when you have several yellows with unknown placement.</li>
</ul>
<h2 id="hard-mode-specific-advice-if-applicable">Hard Mode-specific advice (if applicable)</h2>
<ul>
<li>Use a first guess that is both information-rich and flexible (no repeats, good vowel spread).</li>
<li>Avoid early guesses that “paint you into a corner” with awkward fixed patterns.</li>
<li>If you must reuse yellows, prioritize arranging them into new positions rather than re-testing them in the same forbidden slot.</li>
</ul>
<h2 id="curiosities-and-why-6-letters-feels-different">Curiosities and why 6 letters feels different</h2>
<ul>
<li>The search space grows substantially with word length: an extra letter increases both the number of possible words and the number of positional arrangements, which typically makes deduction take longer.</li>
<li>Feedback is still only three colors, so each guess must do more “work” to reduce uncertainty.</li>
<li>Six-letter versions often create more situations where it’s optimal to <strong>probe</strong> rather than immediately guess a plausible answer.</li>
</ul>
    `,    
    cover: {
      src: synthword6Cover,
      alt: 'Synthword 6 letters cover art'
    },
    playRoute: '/synthword6',
    techSummary: [
      'State-driven React flow tracks screen state, active language, guesses, evaluations, toasts, and win/lose conditions in one loop.',
      'Guess scoring uses a two-pass evaluator: exact matches are marked first, then remaining letters are marked as present to handle duplicates correctly.',
      'Bilingual gameplay switches between dedicated IT and EN six-letter dictionaries while preserving the same puzzle rules and UI flow.',
      'Virtual keyboard buttons and physical keyboard events both call the same handler, ensuring parity across touch and desktop sessions.',
      'Neon synthwave presentation is injected with component-scoped styles, including scanlines, glow effects, flip animations, shake feedback, and win pulses.'
    ],
    gameplayRules: [
      'Find the hidden six-letter word in six guesses.',
      'Interpret tile feedback after every attempt to place letters and remove wrong options.',
      'Switch IT/EN mode as needed and optimize your guess sequence to solve within the limit.'
    ],
    highlights: [
      {
        title: 'Six-letter challenge loop',
        text: 'Each round enforces six-character guesses over six attempts, with per-row status and validation to keep pacing tight and readable.'
      },
      {
        title: 'Evaluation feedback',
        text: 'Tiles and keycaps are updated with correct, present, and absent states after each submit, giving immediate guidance for the next guess.'
      },
      {
        title: 'Input and language parity',
        text: 'Players can swap between Italian and English modes and keep playing through the same controls, whether using the on-screen keyboard or hardware keys.'
      }
    ]
  },
  {
    slug: 'numdle',
    title: 'Numdle',
    tagline: 'A neon math-sequence puzzle where every digit reveals the pattern.',
    description: 'Decode the hidden five-number sequence in six attempts using instant per-cell feedback and escalating logic difficulty. Daily seeded runs keep the challenge fresh while preserving a fast, arcade-style puzzle rhythm.',
    extendedDescription: 'Numdle blends numeric intuition and formal pattern analysis: every response narrows the search space, but higher difficulties require identifying hidden rule structures, not just matching values. Strong solvers combine elimination, sequence theory, and controlled experimentation across attempts.',
    cover: {
      src: numdleCover,
      alt: 'Numdle cover art'
    },
    playRoute: '/numdle',
    techSummary: [
      'React state and memoized handlers coordinate difficulty selection, active cell input, attempt history, and win/lose flow in one synchronized loop.',
      'Seeded daily generation builds valid arithmetic and logic-based sequences across easy, medium, and hard rule pools.',
      'Cell-by-cell evaluation marks correct position, present value, and absent value to guide each next attempt.',
      'Keyboard arrows, numeric keys, and on-screen numpad all feed a shared input pipeline for desktop/mobile parity.',
      'Looping soundtrack playback is sourced from /features/games/numdle/assets/soundtrack.mp3 and is expected to continue seamlessly without resets between levels.'
    ],
    gameplayRules: [
      'Enter a five-number guess for each attempt, following the selected difficulty pattern rules.',
      'Use per-cell feedback to identify numbers in the correct place, present elsewhere, or absent.',
      'Solve the sequence within six attempts, applying logic and pattern deduction each round.'
    ],
    highlights: [
      {
        title: 'Daily-seeded challenge',
        text: 'The sequence generator uses a date-based seed plus difficulty offsets, producing reproducible daily puzzles while keeping each mode distinct.'
      },
      {
        title: 'Two-digit cell input',
        text: 'Each grid cell supports structured one- or two-digit entry with guided cursor movement, preserving speed without sacrificing input clarity.'
      },
      {
        title: 'Progressive hinting',
        text: 'After multiple attempts, a contextual rule hint appears to reduce dead-ends while maintaining the core deduction challenge.'
      }
    ]
  },
  {
    slug: 'astroshoot',
    title: 'Astroshoot',
    tagline: 'A neon Asteroids clone with seamless arcade soundtrack flow.',
    description: 'Pilot a glowing interceptor through dense asteroid waves, split large rocks into smaller threats, and survive long enough to push your score into classic arcade territory.',
    extendedDescription: 'Astroshoot is designed as a faithful Asteroids-style survival loop with modern neon presentation. Movement is inertia-driven, firing cadence is tightly controlled, and every wave asks for precise rotation, momentum management, and risk-aware positioning. The soundtrack is loaded from /features/games/astroshoot/assets/soundtrack.mp3 and loops continuously so it never stops or resets between levels.',
    cover: {
      src: '/features/games/astroshoot/assets/cover.jpg',
      alt: 'Astroshoot cover art'
    },
    playRoute: '/astroshoot',
    techSummary: [
      'Gameplay clone is based on the Asteroids implementation in React + TypeScript with a requestAnimationFrame game loop.',
      'Procedural asteroid geometry is generated per size tier, then split into medium and small fragments on collisions.',
      'Ship handling uses rotational thrust, friction damping, wraparound space bounds, and invulnerability frames after hits.',
      'Keyboard and touch controls share one state model, including a mobile joystick and dedicated fire interaction.',
      'The game soundtrack plays from /features/games/astroshoot/assets/soundtrack.mp3 in loop mode and remains continuous between level transitions.'
    ],
    gameplayRules: [
      'Rotate and thrust to position your ship while preserving momentum and avoiding direct asteroid impact.',
      'Shoot large asteroids to split them, then clear all fragments to complete the current wave.',
      'Survive escalating waves with limited lives and maintain control under increasing screen pressure.'
    ],
    highlights: [
      {
        title: 'Wave progression',
        text: 'When the asteroid list is cleared, the game increments the wave and spawns a larger set of fresh large asteroids to sustain difficulty growth.'
      },
      {
        title: 'Collision and scoring',
        text: 'Bullet-to-asteroid collisions award points by asteroid size and trigger particle explosions for immediate visual confirmation.'
      },
      {
        title: 'Continuous audio behavior',
        text: 'The audio layer is configured to loop without restart, preserving uninterrupted soundtrack flow while players move across levels.'
      }
    ]
  }
];

// Finds the matching game detail entry for the provided slug.
const findGameDetail = (slug) => gameDetails.find((game) => game.slug === slug);

// Renders the technical detail page for a selected game.
export function GameDetail() {
  const { slug } = useParams();
  const game = findGameDetail(slug);

  const navLinks = [
    { href: '/', label: 'Home' },
    { href: '/play-games-now', label: 'Play games now', accent: true }
  ];

  if (!game) {
    return (
      <SharedPageLayout
        brand="GIUGGIOLI.NET"
        links={navLinks}
        footerText="Neon Arcade Vault • Powered by Giuggioli.Net"
      >
        <div className="game-detail__missing">
          <div className="game-detail__missing-title">Game detail not found</div>
          <p className="game-detail__missing-text">
            The arcade file you requested does not exist. Head back to the games vault to choose another title.
          </p>
          <Link className="game-detail__cta" to="/play-games-now">Back to games</Link>
        </div>
      </SharedPageLayout>
    );
  }

  return (
    <SharedPageLayout
      brand="GIUGGIOLI.NET"
      links={navLinks}
      footerText="Neon Arcade Vault • Powered by Giuggioli.Net"
    >
      <section className="game-detail__hero">
        <div className="game-detail__hero-content">
          <p className="game-detail__kicker">Game detail</p>
          <h1 className="game-detail__title">{game.title}</h1>
          <p className="game-detail__tagline">{game.tagline}</p>
          <p className="game-detail__description">{game.description}</p>
          {/* <section className="game-detail__section">
            <div className="game-detail__section-header">
              <h2>Extended description</h2>
            </div>
          </section> */}
          <div className="game-detail__actions">
            <a className="game-detail__cta game-detail__cta--play" href={game.playRoute} target="_blank" rel="noreferrer">
              Play now
            </a>
            <Link className="game-detail__cta game-detail__cta--ghost" to="/play-games-now">
              Back to games
            </Link>
          </div>
        </div>
        <div className="game-detail__cover">
          <img src={game.cover.src} alt={game.cover.alt} />
        </div>
      </section>

      <section className="game-detail__section">
        <div className="game-detail__section-header">
          <h2>How to play</h2>
          <p>Detailed gameplay rules and objective flow.</p>
        </div>
        <ul className="game-detail__list">
          {game.gameplayRules.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>

      <section className="game-detail__section">
        <div className="game-detail__section-header">
          <h2>About the game</h2>
          <p>At the heart of the game</p>
        </div>
        <div className="game-detail__extended" dangerouslySetInnerHTML={{ __html: game.extendedDescription }} />
        </section>

      <section className="game-detail__section">
        <div className="game-detail__section-header">
          <h2>Technical summary</h2>
          <p>Key implementation highlights.</p>
        </div>
        <ul className="game-detail__list">
          {game.techSummary.map((item) => (
            <li key={item}>{item}</li>
          ))}
        </ul>
      </section>



      <section className="game-detail__section game-detail__section--grid">
        {game.highlights.map((item) => (
          <article key={item.title} className="game-detail__card">
            <h3>{item.title}</h3>
            <p>{item.text}</p>
          </article>
        ))}
      </section>
    </SharedPageLayout>
  );
}
